import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles applying a discount to the cart.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    const abortController = this.#createAbortController();
    const status = document.getElementById(`shopify-section-${this.dataset.sectionId}`)
      ?.querySelector('.cart-discount__status');
    if (status) status.textContent = '';

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Failed to update discount');
      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      // A request that was superseded can still resolve. Keep its public event
      // promise settled, but do not let it replace the current cart UI.
      if (this.#activeFetch !== abortController) return;

      const submittedCode = discountCodeValue.trim().toUpperCase();
      const appliedDiscount = data.discount_codes.find(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) =>
          discount.code.toUpperCase() === submittedCode
      );
      if (appliedDiscount?.applicable === false) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          appliedDiscount?.applicable === true
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      const sectionId = this.dataset.sectionId;
      await this.#renderDiscountUpdate(sectionId, newHtml, abortController);

      // Announce only the current, confirmed application. Reuse the translated
      // pill label after rendering; no focus change or optimistic success speech.
      if (this.#activeFetch === abortController && appliedDiscount?.applicable === true) {
        const updatedSection = document.getElementById(`shopify-section-${sectionId}`);
        const appliedPill = Array.from(updatedSection?.querySelectorAll('.cart-discount__pill') || [])
          .find((pill) => pill instanceof HTMLElement && pill.dataset.discountCode?.toUpperCase() === submittedCode);
        const liveStatus = updatedSection?.querySelector('.cart-discount__status');
        if (liveStatus && appliedPill) liveStatus.textContent = appliedPill.getAttribute('aria-label') || '';
      }
    } catch (error) {
      deferredPromise.reject(error);
      if (this.#activeFetch === abortController && error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const status = document.getElementById(`shopify-section-${this.dataset.sectionId}`)
      ?.querySelector('.cart-discount__status');
    if (status) status.textContent = '';
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      // A request that was superseded can still resolve. Keep its public event
      // promise settled, but do not let it replace the current cart UI.
      if (this.#activeFetch !== abortController) return;

      // Removing a focused pill also removes its button. Capture immediately
      // before the morph so a shopper who moved during the request keeps focus.
      const focusedElement = document.activeElement;
      const restoreFocus = focusedElement instanceof HTMLElement && pill.contains(focusedElement);
      const drawer = this.closest('theme-drawer');
      const sectionId = this.dataset.sectionId;

      await this.#renderDiscountUpdate(sectionId, data.sections[sectionId], abortController);

      if (
        restoreFocus &&
        this.#activeFetch === abortController &&
        (!drawer || drawer.hasAttribute('open')) &&
        (document.activeElement === document.body || document.activeElement === focusedElement)
      ) {
        // The disclosure remains available whether the last code was removed
        // or other codes remain, and whether its content is open or collapsed.
        const summary = document.getElementById(`shopify-section-${sectionId}`)
          ?.querySelector('.cart-discount__summary');
        if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
      }
    } catch (error) {
      deferredPromise.reject(error);
      if (this.#activeFetch === abortController && error instanceof Error && error.name !== 'AbortError') {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
    }
  };

  /**
   * Discount changes can change a Shopify line key and replace its quantity input.
   * Capture immediately before rendering so deliberate focus moves during fetch win.
   * @param {string} sectionId
   * @param {string} html
   * @param {AbortController} controller
   */
  async #renderDiscountUpdate(sectionId, html, controller) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    const active = document.activeElement;
    const quantity = active instanceof HTMLInputElement && active.matches('[ref="quantityInput"]') && section?.contains(active)
      ? active : null;
    const identity = quantity?.closest('[data-cart-line-identity]')?.getAttribute('data-cart-line-identity');
    const drawer = this.closest('theme-drawer');

    await morphSection(sectionId, html, { mode: drawer ? 'hydration' : 'full' });

    if (
      !quantity || !identity || quantity.isConnected ||
      this.#activeFetch !== controller || (drawer && !drawer.hasAttribute('open')) ||
      document.activeElement !== document.body
    ) return;

    // Variant + selling plan + properties remain stable when discount keys change.
    // Only restore an unambiguous surviving line, never a different item by index.
    const rows = Array.from(document.getElementById(`shopify-section-${sectionId}`)
      ?.querySelectorAll('[data-cart-line-identity]') || [])
      .filter((row) => row.getAttribute('data-cart-line-identity') === identity);
    if (rows.length !== 1) return;
    const replacement = rows[0]?.querySelector('[ref="quantityInput"]');
    if (replacement instanceof HTMLInputElement && !replacement.disabled) replacement.focus({ preventScroll: true });
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
